import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ErrorCode } from 'src/app/core/dtos/enums/error-code';
import { IContentBase } from 'src/app/core/dtos/interfaces/i-content-base';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { IContentVersionSummary } from 'src/app/core/dtos/interfaces/i-content-version-summary';
import { IErrorResponse } from 'src/app/core/dtos/interfaces/i-error-response';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { IRepurposeContentResponse } from 'src/app/core/dtos/interfaces/i-repurpose-content-response-dto';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { ISpikeContentResponseDto } from 'src/app/core/dtos/interfaces/i-spike-content-response-dto';
import { ExpandableCheckboxListItem } from 'src/app/core/models/i-expandable-checkbox-list-item';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Injectable({
  providedIn: 'root',
})
export class SharedContentTypeService {
  constructor(
    private dialogService: DialogService,
    private snackBar: MatSnackBar
  ) {}

  createNewContent$<T extends IContentBase>(
    createNewFn$: (content: T) => Observable<ISaveContentResponseDto>,
    content: T
  ): Observable<ISaveContentResponseDto> {
    if (content.deliveryTargetCodes.length === 0) {
      this.dialogService.openErrorDialog({
        message: 'Website is required in order to save',
      });
      return EMPTY;
    } else {
      return createNewFn$(content).pipe(
        catchError(() => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving the content. Please contact IT.',
          });
          return EMPTY;
        })
      );
    }
  }

  createNewVersionWithCurrentStateCheck$(
    createVersionFn$: (
      contentId: string,
      versionId: string
    ) => Observable<INewContentVersionResponseDto>,
    deleteVersionFn$: (
      contentId: string,
      versionToDeleteId: string
    ) => Observable<any>,
    contentId: string,
    versionId: string,
    currentDraftVersion: IContentVersionSummary
  ): Observable<INewContentVersionResponseDto> {
    return currentDraftVersion
      ? this.dialogService
          .openConfirmationDialog({
            title: 'Create new version from content?',
            message:
              'This will create a new version and delete the current draft. Are you sure you want to do this?',
            confirmLabel: 'Yes',
            cancelLabel: 'No',
          })
          .afterClosed()
          .pipe(
            filter((dialogResponse) => !!dialogResponse),
            switchMap((_) => {
              return this.deleteAndCreateNewVersion$(
                deleteVersionFn$,
                createVersionFn$,
                contentId,
                versionId,
                currentDraftVersion.id
              );
            })
          )
      : this.createNewVersion$(createVersionFn$, contentId, versionId);
  }

  deleteContent$(
    deleteFn$: (contentId: string) => Observable<any>,
    contentId: string
  ): Observable<any> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Delete content?',
        message:
          'This will delete the current content and all its versions. Are you sure you want to do this?',
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return deleteFn$(contentId).pipe(
            catchError(() => {
              this.dialogService.openErrorDialog({
                message:
                  'An error has occurred whilst deleting the content. Please contact IT.',
              });
              return EMPTY;
            })
          );
        })
      );
  }

  deleteUnpublishedDraft$(
    deleteVersionFn$: (
      contentId: string,
      versionToDeleteId: string
    ) => Observable<any>,
    contentId: string,
    versionId: string
  ): Observable<Object> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Delete unpublished draft version?',
        message:
          'This will delete the current unpublished draft version. Are you sure you want to do this?',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap((_) => {
          return deleteVersionFn$(contentId, versionId).pipe(
            catchError(() => {
              this.dialogService.openErrorDialog({
                message:
                  'An error has occurred whilst deleting the content. Please contact IT.',
              });
              return EMPTY;
            })
          );
        })
      );
  }

  reactivateContent<T>(
    getContentByVersionFn$: (versionId: string) => Observable<T>,
    createVersionFn$: (
      contentId: string,
      versionId: string
    ) => Observable<INewContentVersionResponseDto>,
    deleteVersionFn$: (
      contentId: string,
      versionToDeleteId: string
    ) => Observable<any>,
    contentId: string,
    versionId: string,
    publishedVersionId: string,
    newWebsites: string[],
    currentDraftVersion: IContentVersionSummary,
    deliveryTargets$: Observable<ExpandableCheckboxListItem[]>,
    isPreviouslyPublishedVersion: boolean
  ): Observable<INewContentVersionResponseDto> {
    let obs$;
    if (isPreviouslyPublishedVersion) {
      obs$ = this.handlePreviouslyPublishedVersion$(
        getContentByVersionFn$,
        createVersionFn$,
        deleteVersionFn$,
        contentId,
        versionId,
        publishedVersionId,
        newWebsites,
        currentDraftVersion,
        deliveryTargets$
      );
    } else {
      obs$ = this.createNewVersionWithCurrentStateCheck$(
        createVersionFn$,
        deleteVersionFn$,
        contentId,
        versionId,
        currentDraftVersion
      );
    }

    return obs$;
  }

  repurposeContent$(
    repurposeFn$: (
      contentId: string,
      status: ContentStatus
    ) => Observable<IRepurposeContentResponse>,
    versionId: string
  ): Observable<IRepurposeContentResponse> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Repurpose content?',
        message:
          'This will create a copy of the content without any editions attached.',
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return repurposeFn$(versionId, ContentStatus.WRITING).pipe(
            catchError(() => {
              this.dialogService.openErrorDialog({
                message:
                  'An error has occurred whilst repurposing the article. Please contact IT.',
              });
              return EMPTY;
            })
          );
        })
      );
  }

  saveContent<T extends IContentBase>(
    updateFn$: (id: string, content: T) => Observable<T>,
    getContentFn$: (id: string) => Observable<T>,
    staleRecordFn: (content: T) => void | null,
    currentWebsites: string[],
    newWebsites: string[],
    deliveryTargets$: Observable<ExpandableCheckboxListItem[]>,
    content: T
  ) {
    return this.getMissingWebsites(
      currentWebsites,
      newWebsites,
      deliveryTargets$
    ).pipe(
      switchMap((missingWebsites) => {
        if (missingWebsites.length > 0) {
          return this.dialogService
            .openConfirmationDialog({
              title: 'Save content?',
              message:
                `This change will remove content from the following websites <br><br>` +
                `${missingWebsites.join(', ')}<br><br>` +
                `Are you sure you want to continue to save?<br>` +
                `Note: The links in emails will break for removed sites`,
              confirmLabel: 'Yes',
              cancelLabel: 'No',
            })
            .afterClosed()
            .pipe(
              filter((dialogResponse) => !!dialogResponse),
              switchMap((_) => {
                return this.performSave$(
                  updateFn$,
                  getContentFn$,
                  staleRecordFn,
                  content
                );
              })
            );
        } else {
          return this.performSave$(
            updateFn$,
            getContentFn$,
            staleRecordFn,
            content
          );
        }
      })
    );
  }

  spikeContent$(
    spikeFn$: (contentId: string) => Observable<ISpikeContentResponseDto>,
    contentId: string
  ): Observable<ISpikeContentResponseDto> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Spike content?',
        message:
          'This will delete the draft and spike the current content. Are you sure you want to do this?',
        confirmLabel: 'Yes',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this.dialogService
            .openErrorDialog({
              title: 'Remove from Google',
              message:
                'Please remove the URLs (for each site the content was published to) from Google by visiting https://www.google.com/webmasters/tools/url-removal',
              confirmLabel: 'Ok',
            })
            .afterClosed()
            .pipe(
              switchMap(() => {
                return spikeFn$(contentId).pipe(
                  catchError(() => {
                    this.dialogService.openErrorDialog({
                      message:
                        'An error has occurred whilst deleting the content. Please contact IT.',
                    });
                    return EMPTY;
                  })
                );
              })
            );
        })
      );
  }

  updateState<T>(
    updateStateFn$: (
      id: string,
      action: string
    ) => Observable<IContentStateChangeResponseDto>,
    getContentFn$: (id: string) => Observable<T>,
    setEditMode: (newEditModeValue: boolean) => void,
    id: string,
    stateAction: ContentStatus,
    isEditMode: boolean
  ): Observable<T> {
    return stateAction === ContentStatus.PUBLISHED
      ? this.dialogService
          .openConfirmationDialog({
            title: 'Publish content?',
            message:
              'This will make it live on the websites you have selected.',
            confirmLabel: 'Yes',
            cancelLabel: 'No',
          })
          .afterClosed()
          .pipe(
            filter((dialogResponse) => !!dialogResponse),
            switchMap((_) => {
              return this.performStateChange$(
                updateStateFn$,
                getContentFn$,
                setEditMode,
                id,
                stateAction,
                isEditMode
              );
            })
          )
      : this.performStateChange$(
          updateStateFn$,
          getContentFn$,
          setEditMode,
          id,
          stateAction,
          isEditMode
        );
  }

  private confirmReactivate$(): Observable<boolean> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Reactivate content?',
        message: `Are you sure? You are reactivating an old version of this content. This will overwrite the currently live published version.`,
        confirmLabel: 'Reactivate',
        cancelLabel: 'Cancel',
      })
      .afterClosed();
  }

  private confirmReactivateWithWebsites$(
    missingWebsites: string[]
  ): Observable<boolean> {
    return this.dialogService
      .openConfirmationDialog({
        title: 'Reactivate content?',
        message: `Are you sure? You are reactivating an old version of this content. This will overwrite the currently live published version as well as remove these websites:<br><br>${missingWebsites.join(
          ', '
        )}<br><br>Note: The links in emails will break for removed sites`,
        confirmLabel: 'Reactivate',
        cancelLabel: 'Cancel',
      })
      .afterClosed();
  }

  private createNewVersion$(
    createVersionFn$: (
      contentId: string,
      versionId: string
    ) => Observable<INewContentVersionResponseDto>,
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> {
    return createVersionFn$(contentId, versionId).pipe(
      catchError((error) => {
        let message =
          'An error has occurred whilst creating a new version of the content. Please contact IT.';
        let title = 'Error';

        switch (error?.errorMessage?.errorCode) {
          case ErrorCode.UnpublishedVersionAlreadyExists:
            title = 'Unpublished version already exists';
            message =
              'Another user has already created an unpublished version of this content';
            break;
          default:
            break;
        }

        this.dialogService.openErrorDialog({
          title: title,
          message: message,
        });
        return EMPTY;
      })
    );
  }

  private deleteAndCreateNewVersion$(
    deleteVersionFn$: (
      contentId: string,
      versionToDeleteId: string
    ) => Observable<any>,
    createVersionFn$: (
      contentId: string,
      versionId: string
    ) => Observable<INewContentVersionResponseDto>,
    contentId: string,
    versionToCopyId: string,
    versionToDeleteId: string
  ): Observable<INewContentVersionResponseDto> {
    return deleteVersionFn$(contentId, versionToDeleteId).pipe(
      catchError(() => {
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst deleting version. Please contact IT.',
        });
        return EMPTY;
      }),
      switchMap(() =>
        this.createNewVersion$(createVersionFn$, contentId, versionToCopyId)
      )
    );
  }

  private getMissingWebsites(
    oldWebsites: string[],
    newWebsites: string[],
    deliveryTargets$: Observable<ExpandableCheckboxListItem[]>
  ): Observable<string[]> {
    return deliveryTargets$.pipe(
      map((dt) =>
        oldWebsites
          .filter(
            (website) =>
              !newWebsites.includes(website) && dt.some((p) => p.id === website)
          )
          .map((website) => dt.find((p) => p.id === website)?.label || '')
      )
    );
  }

  private handlePreviouslyPublishedVersion$<T extends IContentBase>(
    getContentByVersionFn$: (versionId: string) => Observable<T>,
    createVersionFn$: (
      contentId: string,
      versionId: string
    ) => Observable<INewContentVersionResponseDto>,
    deleteVersionFn$: (
      contentId: string,
      versionToDeleteId: string
    ) => Observable<any>,
    contentId: string,
    versionId: string,
    publishedVersionId: string,
    newWebsites: string[],
    currentDraftVersion: IContentVersionSummary,
    deliveryTargets$: Observable<ExpandableCheckboxListItem[]>
  ): Observable<INewContentVersionResponseDto> {
    return getContentByVersionFn$(publishedVersionId).pipe(
      switchMap((publishedVersion) => {
        return this.getMissingWebsites(
          publishedVersion.deliveryTargetCodes,
          newWebsites,
          deliveryTargets$
        ).pipe(
          switchMap((missingWebsites) => {
            return missingWebsites.length > 0
              ? this.confirmReactivateWithWebsites$(missingWebsites)
              : this.confirmReactivate$();
          })
        );
      }),
      filter((dialogResponse) => !!dialogResponse),
      switchMap((_) =>
        this.createNewVersionWithCurrentStateCheck$(
          createVersionFn$,
          deleteVersionFn$,
          contentId,
          versionId,
          currentDraftVersion
        )
      )
    );
  }

  private performSave$<T extends IContentBase>(
    updateFn$: (id: string, content: T) => Observable<T>,
    getContentFn$: (id: string) => Observable<T>,
    staleRecordFn: (content: T) => void | null,
    content: T
  ): Observable<T> {
    return updateFn$(content.contentId, content).pipe(
      catchError((error: IErrorResponse) => {
        if (
          error.errorMessage.errorCode === ErrorCode.StaleRecord &&
          staleRecordFn
        ) {
          staleRecordFn(content);
          return EMPTY;
        }
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst saving the content. Please contact IT.',
        });
        return EMPTY;
      }),
      switchMap(() => {
        return getContentFn$(content.contentId);
      })
    );
  }

  private performStateChange$<T>(
    updateStateFn$: (
      id: string,
      action: string
    ) => Observable<IContentStateChangeResponseDto>,
    getContentFn$: (id: string) => Observable<T>,
    setEditMode: (newEditModeValue: boolean) => void,
    id: string,
    stateAction: ContentStatus,
    isEditMode: boolean
  ): Observable<T> {
    const action = stateAction.toString();
    return updateStateFn$(id, action).pipe(
      catchError((error) => {
        let message =
          'An error has occurred whilst saving the content. Please contact IT.';
        let title = 'Error';
        switch (error?.errorMessage?.errorCode) {
          case ErrorCode.StateChangeNotAllowed:
            title = 'Change not allowed';
            message = `You cannot carry out the action ${stateAction} while the content is in the current state`;
            break;
          case ErrorCode.UnpublishedVersionAlreadyExists:
            title = 'Unpublished version already exists';
            message =
              'Another user has already created an unpublished version of this content';
            break;
          default:
            break;
        }
        this.dialogService.openErrorDialog({
          title: title,
          message: message,
        });
        return EMPTY;
      }),
      switchMap((contentStateChange) => {
        this.snackBar.open(
          `Content moved to ${stateAction} successfully`,
          null,
          {
            duration: 5 * 1000,
          }
        );

        if (contentStateChange.currentStateEditable !== isEditMode) {
          setEditMode(contentStateChange.currentStateEditable);
          return EMPTY;
        } else {
          return getContentFn$(id);
        }
      })
    );
  }
}
